import * as H from 'history';
import * as React from 'react';
import { SyntheticEvent } from 'react';
import { Groups } from '../../Enum/Groups';
import { getSolutionForGroup } from '../../Enum/Solution';
import { FilterInterface } from '../../Filter/FilterInterface';
import { FilterEquipService, FilterValueType } from '../../Filter/Serialization/FilterEquipService';
import { UrlEquipService } from '../../Filter/Serialization/UrlEquipService';
import { UrlEquipServiceFactory } from '../../Filter/Serialization/UrlEquipServiceFactory';
import { Product, ProductInterface } from '../../Model/Product';
import { FilterService } from '../../Service/FilterService';
import { TypeUtility } from '../../Service/TypeUtility';
import { ServiceLocator } from '../../ServiceLocator';
import { Loading } from '../Loading';
import { SolutionSubNav } from '../SolutionSubNav';
import { SolutionTabNavigation } from '../SolutionTabNavigation';
import { Trans } from '../Trans';
import { FilterSectionViewProps } from './AbstractFilterSectionView';
import { RowProps } from './AbstractRow';
import { AmplifierFilterView } from './Amplifier/AmplifierFilterView';
import { AmplifierRow } from './Amplifier/AmplifierRow';
import { ElectronicsFilterView } from './Electronics/ElectronicsFilterView';
import { ElectronicsRow } from './Electronics/ElectronicsRow';
import { ForceSensorFilterView } from './ForceSensor/ForceSensorFilterView';
import { ForceSensorRow } from './ForceSensor/ForceSensorRow';
import { LinearActuatorFilterView } from './LinearActuator/LinearActuatorFilterView';
import { LinearActuatorRow } from './LinearActuator/LinearActuatorRow';
import { MaterialSensorFilterView } from './MaterialSensor/MaterialSensorFilterView';
import { MaterialSensorRow } from './MaterialSensor/MaterialSensorRow';
import { MeasuringRollerFilterView } from './MeasuringRoller/MeasuringRollerFilterView';
import { MeasuringRollerRow } from './MeasuringRoller/MeasuringRollerRow';
import { ResultDescription } from './ResultDescription';
import { SteeringFrameFilterView } from './SteeringFrame/SteeringFrameFilterView';
import { SteeringFrameRow } from './SteeringFrame/SteeringFrameRow';
import { TelemetrySystemFilterView } from './TelemetrySystem/TelemetrySystemFilterView';
import { TelemetrySystemRow } from './TelemetrySystem/TelemetrySystemRow';
import { TensionAccessoriesFilterView } from './TensionAccessories/TensionAccessoriesFilterView';
import { TensionAccessoriesRow } from './TensionAccessories/TensionAccessoriesRow';
import { TensionControllerFilterView } from './TensionController/TensionControllerFilterView';
import { TensionControllerRow } from './TensionController/TensionControllerRow';
import { WebGuideAccessoriesFilterView } from './WebGuideAccessories/WebGuideAccessoriesFilterView';
import { WebGuideAccessoriesRow } from './WebGuideAccessories/WebGuideAccessoriesRow';
import { WebGuideControllerFilterView } from './WebGuideController/WebGuideControllerFilterView';
import { WebGuideControllerRow } from './WebGuideController/WebGuideControllerRow';

export interface FinderProps {
    serviceLocator: ServiceLocator
    searchParams: URLSearchParams,
    history: H.History,
}

export interface FinderState {
    rootProducts: ProductInterface[],
    filters: Map<string, FilterInterface<any>>,
    ignoreFilters: boolean
}

function filterArrayToMap<T extends FilterValueType>(
    input: FilterInterface<T>[],
    filterEquipService: FilterEquipService,
    searchParams: URLSearchParams
): Map<string, FilterInterface<T>> {
    const map = new Map<string, FilterInterface<T>>();
    for (const filter of input) {
        map.set(filter.keyPath, filterEquipService.equip(filter, searchParams));
    }

    return map;
}

export abstract class AbstractFinder<T extends FinderProps, S extends FinderState = FinderState>
    extends React.Component<T, S> {
    private filterService: FilterService;
    private urlEquipService: UrlEquipService;

    constructor(props: T, context: any) {
        super(props, context);
        this.handleIgnoreFilterChange = this.handleIgnoreFilterChange.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.prepareRootProducts = this.prepareRootProducts.bind(this);

        const urlEquipServiceFactory: UrlEquipServiceFactory = props.serviceLocator.get('urlEquipServiceFactory');
        this.urlEquipService = urlEquipServiceFactory.create(props.history);

        this.filterService = props.serviceLocator.get('filterService');
        const filterEquipService: FilterEquipService = props.serviceLocator.get('filterEquipService');
        const filters = filterArrayToMap(
            this.getInitialFilters(),
            filterEquipService,
            this.props.searchParams
        );

        this.state = {
            rootProducts: [] as ProductInterface[],
            filters,
            ignoreFilters: false
        } as Readonly<S>;
    }

    public abstract get group(): Groups;

    public render() {
        const group = this.group;
        const rootProducts = this.state.rootProducts;
        const containerId = 'finder-' + group;
        if (!rootProducts || rootProducts.length === 0) {
            return <div className="container-fluid pf-finder pf-finder-loading" id={containerId}>
                {this.renderHeaderSection([], [], [], group)}
                {this.renderLoading()}
            </div>;
        }

        const products = this.collectProducts(rootProducts);
        const filteredProducts = this.sortProducts(this.filterProducts(products));

        return <div className="container-fluid pf-finder" id={containerId}>
            {this.renderHeaderSection(rootProducts, filteredProducts, products, group)}
            {this.renderContent(rootProducts, filteredProducts, products)}
            {this.renderFooter(rootProducts, filteredProducts, products)}
        </div>;
    }

    public componentDidMount(): void {
        const typeUtility: TypeUtility<Product> = this.props.serviceLocator.get('typeUtility');
        typeUtility.getRepositoryForGroups(this.group).findAll().then(models => {
            this.setState({rootProducts: this.prepareRootProducts(models as ProductInterface[])});
        });
    }

    public handleFilterChange(attributeKey: string, newFilter: FilterInterface<any>) {
        console.debug('[AbstractFinder] Filter changed', attributeKey, newFilter);

        this.setState(state => {
            const filters = new Map(state.filters);
            filters.set(attributeKey, newFilter);
            this.urlEquipService.equip(newFilter);

            return {filters};
        });
    }

    /**
     * Prepare the root Products
     *
     * This will be invoked after the Products have been fetched from the server, and before they are assigned to the
     * state.
     *
     * @param {ProductInterface[]} products
     * @return {ProductInterface[]}
     */
    protected prepareRootProducts(products: ProductInterface[]): ProductInterface[] {
        return products;
    }

    protected getInitialFilters(): FilterInterface<any>[] {
        return [
            // FilterService.oneOf('A_PIM_Lagerung_Welle', ['stehende Achse', 'Seilrolle']),
            // FilterService.equals('A_PIM_Lagerung_Welle', 'drehende Welle'),
            // FilterService.equals('A_PIM_Lagerung_Welle', 'Seilrolle'),
            // FilterService.equals('A_PIM_Lagerung_Welle', 'stehende Achse'),
            // FilterService.equals('name', 'LMGZ'),
            // FilterService.range('A_PIM_Nennkraft', {min: 0, max: 1}),
        ];
    }

    protected abstract renderHeadline(
        filteredProducts: ProductInterface[],
        allProducts: ProductInterface[]
    ): JSX.Element ;

    protected renderHeaderSection(
        rootProducts: ProductInterface[],
        filteredProducts: ProductInterface[],
        allProducts: ProductInterface[],
        group: Groups
    ) {
        const activeSolution = getSolutionForGroup(group);

        return <header>
            <div className="pf-header-upper row">
                <SolutionTabNavigation activeSolution={activeSolution}/>
            </div>
            <div className="pf-header-lower row">
                <div className="col-md-4">
                    {/*{this.renderHeadline(filteredProducts, allProducts)}*/}
                    <SolutionSubNav solution={activeSolution} currentGroup={group}/>
                </div>
                <div className="col-md-8">
                    <div className="pf-finder-filter filter-column">
                        {this.renderFilter(group, rootProducts, filteredProducts, allProducts)}
                    </div>
                </div>
            </div>
        </header>;
    }

    protected renderFooter(
        rootProducts: ProductInterface[],
        filteredProducts: ProductInterface[],
        allProducts: ProductInterface[]
    ): JSX.Element | null {
        return null;
    }

    /**
     * Return the Products for each individual row
     *
     * Per default the root Product's children will be returned
     *
     * @param {ProductInterface[]} rootProducts
     * @return {ProductInterface[]}
     */
    protected collectProducts(rootProducts: ProductInterface[]): ProductInterface[] {
        return rootProducts.flatMap(rootProduct => {
            if (!rootProduct.children || rootProduct.children.length === 0) {
                console.warn(`Root product ${rootProduct.name} (#${rootProduct.guid}) has no children`);
            }
            return rootProduct.children;
        });
    }

    protected filterProducts(products: ProductInterface[]): ProductInterface[] {
        if (this.state.ignoreFilters) {
            return products;
        }
        const filters = Array.from(this.state.filters.values());

        return this.filterService.filter(products, filters);
    }

    protected sortProducts(products: ProductInterface[]): ProductInterface[] {
        return products.sort((a, b) => {
            if (a.prio === b.prio) {
                return 0;
            }
            return a.prio > b.prio ? 1 : -1;
        });
    }

    protected renderContent(
        rootProducts: ProductInterface[],
        filteredProducts: ProductInterface[],
        allProducts: ProductInterface[]
    ) {
        const group = this.group;

        return <div className="pf-finder">
            <ResultDescription filteredProducts={filteredProducts} allProducts={allProducts}/>
            <div className="pf-finder-products product-column">
                <div className="pf-table-container">
                    <table className="pf-table">
                        <tbody>
                        {filteredProducts.map(i => this.renderItem(i, group))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>;
    }

    protected renderItem(model: ProductInterface, group: Groups): JSX.Element | null {
        const props: RowProps = {
            product: model,
            key: model.guid
        };
        switch (group) {
            case Groups.SteeringFrame:
                return <SteeringFrameRow {...props}/>;
            case Groups.LinearActuator:
                return <LinearActuatorRow {...props}/>;
            case Groups.ForceSensor:
                return <ForceSensorRow {...props}/>;
            case Groups.WebGuideController:
                return <WebGuideControllerRow  {...props}/>;
            case Groups.TensionController:
                return <TensionControllerRow  {...props}/>;
            case Groups.Amplifier:
                return <AmplifierRow {...props}/>;
            case Groups.MaterialSensor:
                return <MaterialSensorRow {...props}/>;
            case Groups.TensionAccessories:
                return <TensionAccessoriesRow {...props}/>;
            case Groups.WebGuideAccessories:
                return <WebGuideAccessoriesRow {...props}/>;
            case Groups.TelemetrySystem:
                return <TelemetrySystemRow {...props}/>;
            case Groups.MeasuringRoller:
                return <MeasuringRollerRow {...props}/>;
            case Groups.Electronics:
                return <ElectronicsRow {...props}/>;
        }
    }

    protected renderLoading() {
        return <div className="pf-loading-container">
            <Loading><Trans>Loading Data</Trans></Loading>
        </div>;
    }

    protected renderFilter(
        group: Groups,
        rootProducts: ProductInterface[],
        filteredProducts: ProductInterface[],
        allProducts: ProductInterface[]
    ): JSX.Element {
        const props: FilterSectionViewProps = {
            group,
            rootProducts,
            allProducts,
            filteredProducts,
            filters: this.state.filters,
            onFilterChange: this.handleFilterChange,
            onIgnoreFilterChange: this.handleIgnoreFilterChange
        };
        switch (group) {
            case Groups.SteeringFrame:
                return <SteeringFrameFilterView {...props}/>;
            case Groups.LinearActuator:
                return <LinearActuatorFilterView {...props}/>;
            case Groups.ForceSensor:
                return <ForceSensorFilterView {...props}/>;
            case Groups.WebGuideController:
                return <WebGuideControllerFilterView {...props}/>;
            case Groups.TensionController:
                return <TensionControllerFilterView {...props}/>;
            case Groups.Amplifier:
                return <AmplifierFilterView {...props}/>;
            case Groups.MaterialSensor:
                return <MaterialSensorFilterView {...props}/>;
            case Groups.TensionAccessories:
                return <TensionAccessoriesFilterView {...props}/>;
            case Groups.WebGuideAccessories:
                return <WebGuideAccessoriesFilterView {...props}/>;
            case Groups.TelemetrySystem:
                return <TelemetrySystemFilterView {...props}/>;
            case Groups.MeasuringRoller:
                return <MeasuringRollerFilterView {...props}/>;
            case Groups.Electronics:
                return <ElectronicsFilterView {...props}/>;
        }
    }

    private handleIgnoreFilterChange(event: SyntheticEvent) {
        this.setState({ignoreFilters: (event.target as HTMLInputElement).checked});
    }
}
